/**
 * Complaint API Types
 *
 * BE entity: io.alture.eurodrive.entities.complaints.Complaint
 * BE enums: ComplainCause, ComplainStatus
 * API namespace: api.complaints
 *
 * Business rules:
 * - Only ONE complaint can be OPEN per rental case at a time
 * - While any complaint is OPEN, the rental case processStatus = 'COMPLAINT'
 * - When the complaint is CLOSED, rental case reverts to its previous processStatus
 * - Previous processStatus is stored in RentalCaseCustomData.preComplaintProcessStatus
 */

// ==================== Enums ====================

/** Maps to BE: io.alture.eurodrive.entities.complaints.ComplainStatus */
export type ComplaintStatus = 'OPEN' | 'ADDED' | 'CLOSED'

/** Maps to BE: io.alture.eurodrive.entities.complaints.ComplainCause */
export type ComplaintCause = 'TIMELINE' | 'LEASING_APPROVAL' | 'OTHER'

// ==================== Labels & Colors ====================

export const complaintStatusLabels: Record<ComplaintStatus, string> = {
  OPEN: 'Otevřená',
  ADDED: 'Doplněno',
  CLOSED: 'Uzavřená'
}

export const complaintStatusColors: Record<ComplaintStatus, string> = {
  OPEN: 'deep-purple',
  ADDED: 'amber',
  CLOSED: 'grey'
}

export const complaintCauseLabels: Record<ComplaintCause, string> = {
  TIMELINE: 'Časová osa',
  LEASING_APPROVAL: 'Souhlas od leasingové společnosti',
  OTHER: 'Jiné'
}

// ==================== Main Entity ====================

/**
 * Complaint — a complaint/claim case linked to a rental case.
 * Matches BE entity fields. ID is a server-assigned UUID.
 */
export interface Complaint {
  id: string                     // UUID (BE-assigned)
  complaintNumber?: string       // Sequential: e.g. "RK-2026-001" (BE-assigned)
  rentalCaseId: string           // UUID — FK to RentalCase
  cause: ComplaintCause
  description: string
  liquidatorId: string           // UUID — FK to SDE User
  liquidatorName: string         // Snapshot of User.display at assignment time
  status: ComplaintStatus
  closedAt?: number              // Epoch ms — when closed
  closedBy?: string              // User.display who closed
  customData?: Record<string, unknown>
  // Audit fields (populated by BE)
  created?: number
  changed?: number
  createdBy?: string
  changedBy?: string
  ownedBy?: string
}

// ==================== Detailed Response ====================

/**
 * Single item from getAllComplaintsDetailed response.
 * Follows the InsuranceEventDetailItem pattern.
 */
export interface ComplaintDetailItem {
  complaint: Complaint
  rentalCase?: {
    id: string
    caseNumber?: string
    startDate?: number
    endDate?: number
    status?: string
  }
  customer?: {
    id: string
    firstName?: string
    surname?: string
    name?: string
  }
  rentalCar?: {
    id: string
    brand?: string
    type?: string
    licencePlate?: string
  }
}

// ==================== Request Types ====================

export interface CreateComplaintRequest {
  rentalCaseId: string
  cause: ComplaintCause
  description: string
  liquidatorId: string
  liquidatorName: string
}

// ==================== Helper Functions ====================

export const getOpenComplaint = (complaints: Complaint[] | undefined): Complaint | undefined =>
  complaints?.find(c => c.status !== 'CLOSED')

export const hasOpenComplaint = (complaints: Complaint[] | undefined): boolean =>
  complaints?.some(c => c.status !== 'CLOSED') ?? false

export const getComplaintLabel = (c: Complaint): string =>
  `Reklamace – ${complaintCauseLabels[c.cause]} (${complaintStatusLabels[c.status]})`
